import { useEffect, useState } from 'react';
import { useAccount, useReadContract, useWriteContract } from 'wagmi';
import AppPage from 'shared/lib/components/common/AppPage';
import { Display, Text } from 'shared/lib/components/common/Typography';
import { FilledGradientButton } from 'shared/lib/components/common/Buttons';
import useChain from 'shared/lib/hooks/UseChain';
import { getChainLogo } from 'shared/lib/data/constants/ChainSpecific';

const FACTORY_ABI = [
    {
      "type": "constructor",
      "inputs": [
        { "name": "governor", "type": "address", "internalType": "address" },
        { "name": "reserve", "type": "address", "internalType": "address" },
        {
          "name": "oracle",
          "type": "address",
          "internalType": "contract VolatilityOracle"
        },
        {
          "name": "borrowerDeployer",
          "type": "address",
          "internalType": "contract BorrowerDeployer"
        },
        {
          "name": "defaultRateModel",
          "type": "address",
          "internalType": "contract IRateModel"
        }
      ],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "DEFAULT_RATE_MODEL",
      "inputs": [],
      "outputs": [
        { "name": "", "type": "address", "internalType": "contract IRateModel" }
      ],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "GOVERNOR",
      "inputs": [],
      "outputs": [{ "name": "", "type": "address", "internalType": "address" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "LENDER_IMPLEMENTATION",
      "inputs": [],
      "outputs": [{ "name": "", "type": "address", "internalType": "address" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "ORACLE",
      "inputs": [],
      "outputs": [
        {
          "name": "",
          "type": "address",
          "internalType": "contract VolatilityOracle"
        }
      ],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "claimRewards",
      "inputs": [
        {
          "name": "lenders",
          "type": "address[]",
          "internalType": "contract Lender[]"
        },
        { "name": "beneficiary", "type": "address", "internalType": "address" }
      ],
      "outputs": [
        { "name": "earned", "type": "uint256", "internalType": "uint256" }
      ],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "couriers",
      "inputs": [{ "name": "", "type": "uint32", "internalType": "uint32" }],
      "outputs": [
        { "name": "wallet", "type": "address", "internalType": "address" },
        { "name": "cut", "type": "uint16", "internalType": "uint16" }
      ],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "createBorrower",
      "inputs": [
        {
          "name": "pool",
          "type": "address",
          "internalType": "contract IUniswapV3Pool"
        },
        { "name": "owner", "type": "address", "internalType": "address" },
        { "name": "salt", "type": "bytes12", "internalType": "bytes12" }
      ],
      "outputs": [
        {
          "name": "borrower",
          "type": "address",
          "internalType": "contract Borrower"
        }
      ],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "createMarket",
      "inputs": [
        {
          "name": "pool",
          "type": "address",
          "internalType": "contract IUniswapV3Pool"
        }
      ],
      "outputs": [],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "enrollCourier",
      "inputs": [
        { "name": "id", "type": "uint32", "internalType": "uint32" },
        { "name": "cut", "type": "uint16", "internalType": "uint16" }
      ],
      "outputs": [],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "getMarket",
      "inputs": [
        {
          "name": "",
          "type": "address",
          "internalType": "contract IUniswapV3Pool"
        }
      ],
      "outputs": [
        {
          "name": "lender0",
          "type": "address",
          "internalType": "contract Lender"
        },
        {
          "name": "lender1",
          "type": "address",
          "internalType": "contract Lender"
        },
        {
          "name": "borrowerImplementation",
          "type": "address",
          "internalType": "contract Borrower"
        }
      ],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "getParameters",
      "inputs": [
        {
          "name": "",
          "type": "address",
          "internalType": "contract IUniswapV3Pool"
        }
      ],
      "outputs": [
        { "name": "ante", "type": "uint208", "internalType": "uint208" },
        { "name": "nSigma", "type": "uint8", "internalType": "uint8" },
        {
          "name": "manipulationThresholdDivisor",
          "type": "uint8",
          "internalType": "uint8"
        },
        {
          "name": "pausedUntilTime",
          "type": "uint32",
          "internalType": "uint32"
        }
      ],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "governMarketConfig",
      "inputs": [
        {
          "name": "pool",
          "type": "address",
          "internalType": "contract IUniswapV3Pool"
        },
        {
          "name": "config",
          "type": "tuple",
          "internalType": "struct Factory.MarketConfig",
          "components": [
            { "name": "ante", "type": "uint208", "internalType": "uint208" },
            { "name": "nSigma", "type": "uint8", "internalType": "uint8" },
            {
              "name": "manipulationThresholdDivisor",
              "type": "uint8",
              "internalType": "uint8"
            },
            {
              "name": "reserveFactor0",
              "type": "uint8",
              "internalType": "uint8"
            },
            {
              "name": "reserveFactor1",
              "type": "uint8",
              "internalType": "uint8"
            },
            {
              "name": "rateModel0",
              "type": "address",
              "internalType": "contract IRateModel"
            },
            {
              "name": "rateModel1",
              "type": "address",
              "internalType": "contract IRateModel"
            }
          ]
        }
      ],
      "outputs": [],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "governRewardsRate",
      "inputs": [
        {
          "name": "lender",
          "type": "address",
          "internalType": "contract Lender"
        },
        { "name": "rate", "type": "uint64", "internalType": "uint64" }
      ],
      "outputs": [],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "governRewardsToken",
      "inputs": [
        {
          "name": "rewardsToken_",
          "type": "address",
          "internalType": "contract ERC20"
        }
      ],
      "outputs": [],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "isBorrower",
      "inputs": [{ "name": "", "type": "address", "internalType": "address" }],
      "outputs": [{ "name": "", "type": "bool", "internalType": "bool" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "pause",
      "inputs": [
        {
          "name": "pool",
          "type": "address",
          "internalType": "contract IUniswapV3Pool"
        },
        { "name": "oracleSeed", "type": "uint40", "internalType": "uint40" }
      ],
      "outputs": [],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "peer",
      "inputs": [{ "name": "", "type": "address", "internalType": "address" }],
      "outputs": [{ "name": "", "type": "address", "internalType": "address" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "rewardsToken",
      "inputs": [],
      "outputs": [
        { "name": "", "type": "address", "internalType": "contract ERC20" }
      ],
      "stateMutability": "view"
    },
    {
      "type": "event",
      "name": "CreateBorrower",
      "inputs": [
        {
          "name": "pool",
          "type": "address",
          "indexed": true,
          "internalType": "contract IUniswapV3Pool"
        },
        {
          "name": "owner",
          "type": "address",
          "indexed": true,
          "internalType": "address"
        },
        {
          "name": "account",
          "type": "address",
          "indexed": false,
          "internalType": "contract Borrower"
        }
      ],
      "anonymous": false
    },
    {
      "type": "event",
      "name": "CreateMarket",
      "inputs": [
        {
          "name": "pool",
          "type": "address",
          "indexed": true,
          "internalType": "contract IUniswapV3Pool"
        },
        {
          "name": "lender0",
          "type": "address",
          "indexed": false,
          "internalType": "contract Lender"
        },
        {
          "name": "lender1",
          "type": "address",
          "indexed": false,
          "internalType": "contract Lender"
        }
      ],
      "anonymous": false
    },
    {
      "type": "event",
      "name": "EnrollCourier",
      "inputs": [
        {
          "name": "id",
          "type": "uint32",
          "indexed": true,
          "internalType": "uint32"
        },
        {
          "name": "wallet",
          "type": "address",
          "indexed": true,
          "internalType": "address"
        },
        {
          "name": "cut",
          "type": "uint16",
          "indexed": false,
          "internalType": "uint16"
        }
      ],
      "anonymous": false
    },
    {
      "type": "event",
      "name": "SetMarketConfig",
      "inputs": [
        {
          "name": "pool",
          "type": "address",
          "indexed": true,
          "internalType": "contract IUniswapV3Pool"
        },
        {
          "name": "config",
          "type": "tuple",
          "indexed": false,
          "internalType": "struct Factory.MarketConfig",
          "components": [
            { "name": "ante", "type": "uint208", "internalType": "uint208" },
            { "name": "nSigma", "type": "uint8", "internalType": "uint8" },
            {
              "name": "manipulationThresholdDivisor",
              "type": "uint8",
              "internalType": "uint8"
            },
            {
              "name": "reserveFactor0",
              "type": "uint8",
              "internalType": "uint8"
            },
            {
              "name": "reserveFactor1",
              "type": "uint8",
              "internalType": "uint8"
            },
            {
              "name": "rateModel0",
              "type": "address",
              "internalType": "contract IRateModel"
            },
            {
              "name": "rateModel1",
              "type": "address",
              "internalType": "contract IRateModel"
            }
          ]
        }
      ],
      "anonymous": false
    }
  ];
   
const LENDER_ABI = [
    {
      "type": "constructor",
      "inputs": [
        { "name": "reserve", "type": "address", "internalType": "address" }
      ],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "DOMAIN_SEPARATOR",
      "inputs": [],
      "outputs": [{ "name": "", "type": "bytes32", "internalType": "bytes32" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "FACTORY",
      "inputs": [],
      "outputs": [
        { "name": "", "type": "address", "internalType": "contract Factory" }
      ],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "RESERVE",
      "inputs": [],
      "outputs": [{ "name": "", "type": "address", "internalType": "address" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "accrueInterest",
      "inputs": [],
      "outputs": [{ "name": "", "type": "uint72", "internalType": "uint72" }],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "allowance",
      "inputs": [
        { "name": "", "type": "address", "internalType": "address" },
        { "name": "", "type": "address", "internalType": "address" }
      ],
      "outputs": [{ "name": "", "type": "uint256", "internalType": "uint256" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "approve",
      "inputs": [
        { "name": "spender", "type": "address", "internalType": "address" },
        { "name": "shares", "type": "uint256", "internalType": "uint256" }
      ],
      "outputs": [{ "name": "", "type": "bool", "internalType": "bool" }],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "asset",
      "inputs": [],
      "outputs": [
        { "name": "", "type": "address", "internalType": "contract ERC20" }
      ],
      "stateMutability": "pure"
    },
    {
      "type": "function",
      "name": "balanceOf",
      "inputs": [
        { "name": "account", "type": "address", "internalType": "address" }
      ],
      "outputs": [{ "name": "", "type": "uint256", "internalType": "uint256" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "balances",
      "inputs": [{ "name": "", "type": "address", "internalType": "address" }],
      "outputs": [{ "name": "", "type": "uint256", "internalType": "uint256" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "borrow",
      "inputs": [
        { "name": "amount", "type": "uint256", "internalType": "uint256" },
        { "name": "recipient", "type": "address", "internalType": "address" }
      ],
      "outputs": [
        { "name": "units", "type": "uint256", "internalType": "uint256" }
      ],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "borrowBalance",
      "inputs": [
        { "name": "account", "type": "address", "internalType": "address" }
      ],
      "outputs": [{ "name": "", "type": "uint256", "internalType": "uint256" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "borrowBalanceStored",
      "inputs": [
        { "name": "account", "type": "address", "internalType": "address" }
      ],
      "outputs": [{ "name": "", "type": "uint256", "internalType": "uint256" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "borrowBase",
      "inputs": [],
      "outputs": [{ "name": "", "type": "uint184", "internalType": "uint184" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "borrowIndex",
      "inputs": [],
      "outputs": [{ "name": "", "type": "uint72", "internalType": "uint72" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "borrows",
      "inputs": [{ "name": "", "type": "address", "internalType": "address" }],
      "outputs": [{ "name": "", "type": "uint256", "internalType": "uint256" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "claimRewards",
      "inputs": [
        { "name": "owner", "type": "address", "internalType": "address" }
      ],
      "outputs": [
        { "name": "earned", "type": "uint96", "internalType": "uint96" }
      ],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "convertToAssets",
      "inputs": [
        { "name": "shares", "type": "uint256", "internalType": "uint256" }
      ],
      "outputs": [{ "name": "", "type": "uint256", "internalType": "uint256" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "convertToShares",
      "inputs": [
        { "name": "assets", "type": "uint256", "internalType": "uint256" }
      ],
      "outputs": [{ "name": "", "type": "uint256", "internalType": "uint256" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "courierOf",
      "inputs": [
        { "name": "account", "type": "address", "internalType": "address" }
      ],
      "outputs": [{ "name": "", "type": "uint32", "internalType": "uint32" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "decimals",
      "inputs": [],
      "outputs": [{ "name": "", "type": "uint8", "internalType": "uint8" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "deposit",
      "inputs": [
        { "name": "amount", "type": "uint256", "internalType": "uint256" },
        { "name": "beneficiary", "type": "address", "internalType": "address" }
      ],
      "outputs": [
        { "name": "shares", "type": "uint256", "internalType": "uint256" }
      ],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "deposit",
      "inputs": [
        { "name": "amount", "type": "uint256", "internalType": "uint256" },
        { "name": "beneficiary", "type": "address", "internalType": "address" },
        { "name": "courierId", "type": "uint32", "internalType": "uint32" }
      ],
      "outputs": [
        { "name": "shares", "type": "uint256", "internalType": "uint256" }
      ],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "initialize",
      "inputs": [],
      "outputs": [],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "lastAccrualTime",
      "inputs": [],
      "outputs": [{ "name": "", "type": "uint32", "internalType": "uint32" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "lastBalance",
      "inputs": [],
      "outputs": [{ "name": "", "type": "uint112", "internalType": "uint112" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "maxDeposit",
      "inputs": [{ "name": "", "type": "address", "internalType": "address" }],
      "outputs": [{ "name": "", "type": "uint256", "internalType": "uint256" }],
      "stateMutability": "pure"
    },
    {
      "type": "function",
      "name": "maxMint",
      "inputs": [{ "name": "", "type": "address", "internalType": "address" }],
      "outputs": [{ "name": "", "type": "uint256", "internalType": "uint256" }],
      "stateMutability": "pure"
    },
    {
      "type": "function",
      "name": "maxRedeem",
      "inputs": [
        { "name": "owner", "type": "address", "internalType": "address" }
      ],
      "outputs": [{ "name": "", "type": "uint256", "internalType": "uint256" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "maxWithdraw",
      "inputs": [
        { "name": "owner", "type": "address", "internalType": "address" }
      ],
      "outputs": [{ "name": "", "type": "uint256", "internalType": "uint256" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "mint",
      "inputs": [
        { "name": "shares", "type": "uint256", "internalType": "uint256" },
        { "name": "beneficiary", "type": "address", "internalType": "address" }
      ],
      "outputs": [
        { "name": "amount", "type": "uint256", "internalType": "uint256" }
      ],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "name",
      "inputs": [],
      "outputs": [{ "name": "", "type": "string", "internalType": "string" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "nonces",
      "inputs": [{ "name": "", "type": "address", "internalType": "address" }],
      "outputs": [{ "name": "", "type": "uint256", "internalType": "uint256" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "peer",
      "inputs": [],
      "outputs": [{ "name": "", "type": "address", "internalType": "address" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "permit",
      "inputs": [
        { "name": "owner", "type": "address", "internalType": "address" },
        { "name": "spender", "type": "address", "internalType": "address" },
        { "name": "value", "type": "uint256", "internalType": "uint256" },
        { "name": "deadline", "type": "uint256", "internalType": "uint256" },
        { "name": "v", "type": "uint8", "internalType": "uint8" },
        { "name": "r", "type": "bytes32", "internalType": "bytes32" },
        { "name": "s", "type": "bytes32", "internalType": "bytes32" }
      ],
      "outputs": [],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "previewDeposit",
      "inputs": [
        { "name": "assets", "type": "uint256", "internalType": "uint256" }
      ],
      "outputs": [{ "name": "", "type": "uint256", "internalType": "uint256" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "previewMint",
      "inputs": [
        { "name": "shares", "type": "uint256", "internalType": "uint256" }
      ],
      "outputs": [{ "name": "", "type": "uint256", "internalType": "uint256" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "previewRedeem",
      "inputs": [
        { "name": "shares", "type": "uint256", "internalType": "uint256" }
      ],
      "outputs": [{ "name": "", "type": "uint256", "internalType": "uint256" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "previewWithdraw",
      "inputs": [
        { "name": "assets", "type": "uint256", "internalType": "uint256" }
      ],
      "outputs": [{ "name": "", "type": "uint256", "internalType": "uint256" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "principleOf",
      "inputs": [
        { "name": "account", "type": "address", "internalType": "address" }
      ],
      "outputs": [{ "name": "", "type": "uint256", "internalType": "uint256" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "rateModel",
      "inputs": [],
      "outputs": [
        { "name": "", "type": "address", "internalType": "contract IRateModel" }
      ],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "redeem",
      "inputs": [
        { "name": "shares", "type": "uint256", "internalType": "uint256" },
        { "name": "recipient", "type": "address", "internalType": "address" },
        { "name": "owner", "type": "address", "internalType": "address" }
      ],
      "outputs": [
        { "name": "amount", "type": "uint256", "internalType": "uint256" }
      ],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "repay",
      "inputs": [
        { "name": "amount", "type": "uint256", "internalType": "uint256" },
        { "name": "beneficiary", "type": "address", "internalType": "address" }
      ],
      "outputs": [
        { "name": "units", "type": "uint256", "internalType": "uint256" }
      ],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "reserveFactor",
      "inputs": [],
      "outputs": [{ "name": "", "type": "uint8", "internalType": "uint8" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "rewardsOf",
      "inputs": [
        { "name": "account", "type": "address", "internalType": "address" }
      ],
      "outputs": [{ "name": "", "type": "uint96", "internalType": "uint96" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "rewardsRate",
      "inputs": [],
      "outputs": [
        { "name": "rate", "type": "uint64", "internalType": "uint64" }
      ],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "setRateModelAndReserveFactor",
      "inputs": [
        {
          "name": "rateModel_",
          "type": "address",
          "internalType": "contract IRateModel"
        },
        { "name": "reserveFactor_", "type": "uint8", "internalType": "uint8" }
      ],
      "outputs": [],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "setRewardsRate",
      "inputs": [
        { "name": "rate", "type": "uint64", "internalType": "uint64" }
      ],
      "outputs": [],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "stats",
      "inputs": [],
      "outputs": [
        { "name": "", "type": "uint72", "internalType": "uint72" },
        { "name": "", "type": "uint256", "internalType": "uint256" },
        { "name": "", "type": "uint256", "internalType": "uint256" },
        { "name": "", "type": "uint256", "internalType": "uint256" }
      ],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "supportsInterface",
      "inputs": [
        { "name": "interfaceId", "type": "bytes4", "internalType": "bytes4" }
      ],
      "outputs": [{ "name": "", "type": "bool", "internalType": "bool" }],
      "stateMutability": "pure"
    },
    {
      "type": "function",
      "name": "symbol",
      "inputs": [],
      "outputs": [{ "name": "", "type": "string", "internalType": "string" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "totalAssets",
      "inputs": [],
      "outputs": [{ "name": "", "type": "uint256", "internalType": "uint256" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "totalSupply",
      "inputs": [],
      "outputs": [{ "name": "", "type": "uint112", "internalType": "uint112" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "transfer",
      "inputs": [
        { "name": "to", "type": "address", "internalType": "address" },
        { "name": "shares", "type": "uint256", "internalType": "uint256" }
      ],
      "outputs": [{ "name": "", "type": "bool", "internalType": "bool" }],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "transferFrom",
      "inputs": [
        { "name": "from", "type": "address", "internalType": "address" },
        { "name": "to", "type": "address", "internalType": "address" },
        { "name": "shares", "type": "uint256", "internalType": "uint256" }
      ],
      "outputs": [{ "name": "", "type": "bool", "internalType": "bool" }],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "underlyingBalance",
      "inputs": [
        { "name": "account", "type": "address", "internalType": "address" }
      ],
      "outputs": [{ "name": "", "type": "uint256", "internalType": "uint256" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "underlyingBalanceStored",
      "inputs": [
        { "name": "account", "type": "address", "internalType": "address" }
      ],
      "outputs": [{ "name": "", "type": "uint256", "internalType": "uint256" }],
      "stateMutability": "view"
    },
    {
      "type": "function",
      "name": "whitelist",
      "inputs": [
        { "name": "borrower", "type": "address", "internalType": "address" }
      ],
      "outputs": [],
      "stateMutability": "nonpayable"
    },
    {
      "type": "function",
      "name": "withdraw",
      "inputs": [
        { "name": "amount", "type": "uint256", "internalType": "uint256" },
        { "name": "recipient", "type": "address", "internalType": "address" },
        { "name": "owner", "type": "address", "internalType": "address" }
      ],
      "outputs": [
        { "name": "shares", "type": "uint256", "internalType": "uint256" }
      ],
      "stateMutability": "nonpayable"
    },
    {
      "type": "event",
      "name": "Approval",
      "inputs": [
        {
          "name": "owner",
          "type": "address",
          "indexed": true,
          "internalType": "address"
        },
        {
          "name": "spender",
          "type": "address",
          "indexed": true,
          "internalType": "address"
        },
        {
          "name": "amount",
          "type": "uint256",
          "indexed": false,
          "internalType": "uint256"
        }
      ],
      "anonymous": false
    },
    {
      "type": "event",
      "name": "Borrow",
      "inputs": [
        {
          "name": "caller",
          "type": "address",
          "indexed": true,
          "internalType": "address"
        },
        {
          "name": "recipient",
          "type": "address",
          "indexed": true,
          "internalType": "address"
        },
        {
          "name": "amount",
          "type": "uint256",
          "indexed": false,
          "internalType": "uint256"
        },
        {
          "name": "units",
          "type": "uint256",
          "indexed": false,
          "internalType": "uint256"
        }
      ],
      "anonymous": false
    },
    {
      "type": "event",
      "name": "CreditCourier",
      "inputs": [
        {
          "name": "id",
          "type": "uint32",
          "indexed": true,
          "internalType": "uint32"
        },
        {
          "name": "account",
          "type": "address",
          "indexed": true,
          "internalType": "address"
        }
      ],
      "anonymous": false
    },
    {
      "type": "event",
      "name": "Deposit",
      "inputs": [
        {
          "name": "caller",
          "type": "address",
          "indexed": true,
          "internalType": "address"
        },
        {
          "name": "owner",
          "type": "address",
          "indexed": true,
          "internalType": "address"
        },
        {
          "name": "assets",
          "type": "uint256",
          "indexed": false,
          "internalType": "uint256"
        },
        {
          "name": "shares",
          "type": "uint256",
          "indexed": false,
          "internalType": "uint256"
        }
      ],
      "anonymous": false
    },
    {
      "type": "event",
      "name": "Repay",
      "inputs": [
        {
          "name": "caller",
          "type": "address",
          "indexed": true,
          "internalType": "address"
        },
        {
          "name": "beneficiary",
          "type": "address",
          "indexed": true,
          "internalType": "address"
        },
        {
          "name": "amount",
          "type": "uint256",
          "indexed": false,
          "internalType": "uint256"
        },
        {
          "name": "units",
          "type": "uint256",
          "indexed": false,
          "internalType": "uint256"
        }
      ],
      "anonymous": false
    },
    {
      "type": "event",
      "name": "RewardsClaimed",
      "inputs": [
        {
          "name": "user",
          "type": "address",
          "indexed": true,
          "internalType": "address"
        },
        {
          "name": "amount",
          "type": "uint96",
          "indexed": false,
          "internalType": "uint96"
        }
      ],
      "anonymous": false
    },
    {
      "type": "event",
      "name": "RewardsRateSet",
      "inputs": [
        {
          "name": "rate",
          "type": "uint64",
          "indexed": false,
          "internalType": "uint64"
        }
      ],
      "anonymous": false
    },
    {
      "type": "event",
      "name": "Transfer",
      "inputs": [
        {
          "name": "from",
          "type": "address",
          "indexed": true,
          "internalType": "address"
        },
        {
          "name": "to",
          "type": "address",
          "indexed": true,
          "internalType": "address"
        },
        {
          "name": "amount",
          "type": "uint256",
          "indexed": false,
          "internalType": "uint256"
        }
      ],
      "anonymous": false
    },
    {
      "type": "event",
      "name": "Withdraw",
      "inputs": [
        {
          "name": "caller",
          "type": "address",
          "indexed": true,
          "internalType": "address"
        },
        {
          "name": "receiver",
          "type": "address",
          "indexed": true,
          "internalType": "address"
        },
        {
          "name": "owner",
          "type": "address",
          "indexed": true,
          "internalType": "address"
        },
        {
          "name": "assets",
          "type": "uint256",
          "indexed": false,
          "internalType": "uint256"
        },
        {
          "name": "shares",
          "type": "uint256",
          "indexed": false,
          "internalType": "uint256"
        }
      ],
      "anonymous": false
    }
  ];

  // Define factory address
  const FACTORY_ADDRESS = '0xEa561C8ca26675F2337546BaDfAF10CeF5e02731' as const;
  
  // Define your lender addresses
  const LENDER_ADDRESSES = [
    '0x9c06739dBB6118551cCCeb00553fEAf3568ca107', // HONEY+
    '0x954d1AD7db7AF19Fa9ba88E3b1a3012D458cE108', // WBERA+
  ] as const;
  
  export default function RewardsPage(): JSX.Element {
    const activeChain = useChain();
    const { address: userAddress } = useAccount();
    const [totalRewards, setTotalRewards] = useState<string>('0');
    const [isLoading, setIsLoading] = useState(false);
  
    // Define individual hooks at the top level
    const rewards0 = useReadContract({
      address: LENDER_ADDRESSES[0] as `0x${string}`,
      abi: LENDER_ABI,
      functionName: 'rewardsOf',
      args: [userAddress],
      query: {
        enabled: Boolean(userAddress),
        refetchInterval: 60000,
      },
    });
  
    const rewards1 = useReadContract({
      address: LENDER_ADDRESSES[1] as `0x${string}`,
      abi: LENDER_ABI,
      functionName: 'rewardsOf',
      args: [userAddress],
      query: {
        enabled: Boolean(userAddress),
        refetchInterval: 60000,
      },
    });

    // Format data for display
    const formattedRewards = [
        { address: LENDER_ADDRESSES[0], data: rewards0.data, poolName: "Kodiak V3 WBERA/HONEY 0.30% (HONEY+)" },
        { address: LENDER_ADDRESSES[1], data: rewards1.data, poolName: "Kodiak V3 WBERA/HONEY 0.30% (WBERA+)" },
    ];

    // Combine rewards queries into an array
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const rewardsQueries = [rewards0, rewards1]; // Add more as needed

    useEffect(() => {
        const calculateTotal = () => {
        const total = rewardsQueries.reduce((acc, query) => {
            if (query.data) {
            return acc + BigInt(query.data.toString());
            }
            return acc;
        }, BigInt(0));
        setTotalRewards(total.toString());
        };

        calculateTotal();
    }, [rewardsQueries]);
  
    // Calculate total rewards
    useEffect(() => {
      const calculateTotal = () => {
        const total = rewardsQueries.reduce((acc, query) => {
          if (query.data) {
            return acc + BigInt(query.data.toString());
          }
          return acc;
        }, BigInt(0));
        setTotalRewards(total.toString());
      };
  
      calculateTotal();
    }, [rewardsQueries]);
  
    const { writeContractAsync: claimRewards } = useWriteContract();
  
    const handleClaim = async () => {
      if (!userAddress) return;
  
      setIsLoading(true);
      try {
        await claimRewards({
          address: FACTORY_ADDRESS as `0x${string}`,
          abi: FACTORY_ABI,
          functionName: 'claimRewards',
          args: [LENDER_ADDRESSES, userAddress],
        });
      } catch (error) {
        console.error('Failed to claim rewards:', error);
      } finally {
        setIsLoading(false);
      }
    };
  
    const activeChainLogo = getChainLogo(activeChain.id, 32);
  
    return (
      <>
        <AppPage>
          <div className='flex flex-col gap-4 max-w-screen-xl m-auto'>
            <div className='flex flex-row items-center gap-4 mb-4'>
              {activeChainLogo}
              <Text size='XXL'>Stacking Salmon Rewards</Text>
            </div>
  
            <div className='flex flex-col gap-4 p-6 bg-gray-800 rounded-lg'>
              <div className='mt-4'>
                <Text size='M' weight='bold' color='#EC5D2A'>Total Available Rewards</Text>
                <Display size='S' weight='semibold'>
                  {totalRewards} BERA
                </Display>
              </div>
  
              {/* Individual rewards per lender */}
              <div className='mt-2'>
                {formattedRewards.map((reward) => (
                    <div key={reward.address} className='flex justify-between items-center py-2'>
                    <Text size='S'>{reward.poolName}</Text>
                    <Text size='S'>{reward.data?.toString() || '0'} BERA</Text>
                    </div>
                ))}
            </div>
  
              <FilledGradientButton
                size='M'
                fillWidth={true}
                disabled={!userAddress || totalRewards === '0' || isLoading}
                onClick={handleClaim}
              >
                {isLoading ? 'Claiming...' : 'Claim All Rewards'}
              </FilledGradientButton>
            </div>
          </div>
        </AppPage>
      </>
    );
  }
